import React, { FunctionComponent, useState } from "react";

/* Validation */
import { Formik, useFormikContext } from "formik";
import validationSchema, { FormData } from "./formConfig";

/* API */
import { QUEUEID } from "./queue-id-constants";
import { STATUS_MESSAGE } from "./statusMessages";
import {
  CallMeResult,
  getHttpStatusAndJson,
  sendCallMe,
} from "./callMe/sendCallMe";
import {
  Alert,
  Button,
  Flex,
  Input,
  InputGroup,
  Label,
  Legend,
  Text,
  Title,
  ValidationMessage,
} from "@gjensidige/builders-components";

const CallMe = () => {
  const [queueId, setQueueId] = useState<QUEUEID>();
  const [statusMessage, setStatusMessage] = useState<string>("");
  const [statusCode, setStatusCode] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [formData, setFormData] = useState<FormData>();

  const handleFormData = (formData: FormData) => {
    setFormData(formData);
    setIsLoading(true);
    setStatusCode("");
    queueId &&
      sendCallMe(
        {
          phoneNumber: formData.phoneNumber,
          customerName: formData.name,
        },
        queueId,
      )
        .then((result) =>
          getHttpStatusAndJson(result).then((parsed) => setResults(parsed)),
        )
        .catch(() => {
          setStatusCode("-1");
          setStatusMessage(STATUS_MESSAGE.ERROR);
        });
  };

  const setResults = (callMeResult: CallMeResult) => {
    setStatusCode(callMeResult.data.code);
    setIsLoading(false);
    switch (callMeResult.data.code) {
      case "0":
        setStatusMessage(STATUS_MESSAGE.OK);
        break;
      case "3001":
        setStatusMessage(STATUS_MESSAGE.INVALID_PHONE);
        break;
      case "2010":
        setStatusMessage(STATUS_MESSAGE.ALREADY_REGISTERED);
        break;
      case "4005":
        setStatusMessage(STATUS_MESSAGE.CLOSED);
        break;
      case "-1":
      case "1200":
      case "15":
        setStatusMessage(STATUS_MESSAGE.ERROR);
        break;
    }
  };

  return (
    <Flex center={"md"} fill layout={1}>
      {statusCode === "0" ? (
        <Alert severity="success" tracking={{eventLabel: 'tak-for-din-henvendelse'}} style={{ whiteSpace: "pre-line" }}>
          <Title>Tak for din henvendelse</Title>
          <Text>
            {`Vi kontakter dig hurtigst muligt i tidsrummet man-tor: ${
              formData?.subject === QUEUEID.PrivatForsikring
                ? "10:00-18:00 og fre 10:00-15:30."
                : "8:30-18:00 og fre 8:30-16:00."
            }`}
          </Text>
        </Alert>
      ) : (
        <Formik
          initialValues={{
            name: "",
            phoneNumber: "",
            isCustomer: "",
            subject: "",
            injurySubject: "",
          }}
          validationSchema={validationSchema}
          onSubmit={(values: FormData) => {
            handleFormData(values);
          }}
        >
          <RenderForm
            setQueueId={setQueueId}
            queueId={queueId}
            loading={isLoading}
          />
        </Formik>
      )}
      {statusCode !== "0" && statusCode !== "" && (
        <Alert severity="error" style={{ whiteSpace: "pre-line" }} tracking={{eventLabel: 'der-skete-en-fejl'}}>
          <Title>Der skete en fejl.</Title>
          <Text>{statusMessage}</Text>
        </Alert>
      )}
    </Flex>
  );
};

interface RenderProps {
  setQueueId: (value: QUEUEID) => void;
  queueId?: QUEUEID;
  loading?: boolean;
}

const RenderForm: FunctionComponent<RenderProps> = ({
  queueId,
  setQueueId,
  loading,
}) => {
  const { handleBlur, setFieldValue, touched, errors, values, handleSubmit } =
    useFormikContext<FormData>();

  return (
    <form onSubmit={handleSubmit}>
      <Flex layout={1}>
        <div>
          <Label htmlFor={"name"}>Navn *</Label>
          <Input
            required
            id="name"
            name="name"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setFieldValue("name", event.target.value);
            }}
            onBlur={handleBlur}
            invalid={!!(touched.name && errors.name)}
            value={values.name}
          />
          <ValidationMessage
            htmlFor={"name"}
            hidden={!(touched.name && errors.name)}
            invalid={!!(touched.name && errors.name)}
          >
            {errors.name}
          </ValidationMessage>
        </div>
        <div>
          <Label htmlFor={"phoneNumber"}>Telefonnummer *</Label>
          <Input
            id="phoneNumber"
            name="phoneNumber"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setFieldValue("phoneNumber", event.target.value);
            }}
            onBlur={handleBlur}
            invalid={!!(touched.phoneNumber && errors.phoneNumber)}
            value={values.phoneNumber}
          />
          <ValidationMessage
            htmlFor={"phoneNumber"}
            hidden={!(touched.phoneNumber && errors.phoneNumber)}
            invalid={!!(touched.phoneNumber && errors.phoneNumber)}
          >
            {errors.phoneNumber}
          </ValidationMessage>
        </div>

        <InputGroup
          variant={"column"}
          id="isCustomer"
          name="isCustomer"
          onChange={(value) => {
            setFieldValue("isCustomer", value.at(0));
          }}
          type="radio"
          value={values.isCustomer}
          onBlur={handleBlur}
          invalid={!!(touched.isCustomer && errors.isCustomer)}
        >
          <Legend>Er du kunde hos os i dag? *</Legend>
          <InputGroup.Input label="Ja" value="Ja" />
          <InputGroup.Input label="Nej" value="Nej" />
          <ValidationMessage
            htmlFor={"isCustomer"}
            hidden={!(touched.isCustomer && errors.isCustomer)}
            invalid={!!(touched.isCustomer && errors.isCustomer)}
          >
            {errors.isCustomer}
          </ValidationMessage>
        </InputGroup>

        {values.isCustomer === "Ja" ? (
          <InputGroup
            variant={"column"}
            id="radio-subject-customer"
            name="subject"
            onChange={(value) => {
              if (value.at(0) !== "injury") {
                setQueueId(value.at(0) as QUEUEID);
              }
              setFieldValue("subject", value.at(0));
            }}
            type="radio"
            value={values.subject}
            onBlur={handleBlur}
            invalid={!!(touched.subject && errors.subject)}
          >
            <Legend>Hvad drejer din henvendelse sig om? *</Legend>
            <InputGroup.Input
              label="Skade, tyveri eller uheld"
              value="injury"
            />
            <InputGroup.Input
              label="Sundhedsforsikring"
              value={QUEUEID.SundhedsForsikring}
            />
            <InputGroup.Input
              label="Privatforsikring"
              value={QUEUEID.PrivatForsikring}
            />

            <ValidationMessage
              htmlFor={"radio-subject-customer"}
              hidden={!(touched.subject && errors.subject)}
              invalid={!!(touched.subject && errors.subject)}
            >
              {errors.subject}
            </ValidationMessage>
          </InputGroup>
        ) : (
          values.isCustomer === "Nej" && (
            <InputGroup
              variant={"column"}
              id="radio-subject-non-customer"
              name="subject"
              onChange={(value) => {
                if (value.at(0) !== "injury") {
                  setQueueId(value.at(0) as QUEUEID);
                }
                setFieldValue("subject", value.at(0));
              }}
              type="radio"
              value={values.subject}
              onBlur={handleBlur}
              invalid={!!(touched.subject && errors.subject)}
            >
              <Legend>Hvad kan vi hjælpe dig med? *</Legend>
              <InputGroup.Input
                label="Tilbud på en Sundhedforsikring"
                value={QUEUEID.SundhedsForsikring}
              />
              <InputGroup.Input
                label="Tilbud på alle andre forsikringer"
                value={QUEUEID.Salg}
              />
              <InputGroup.Input
                label="Henvendelse på vegne af en anden kunde"
                value={QUEUEID.PrivatForsikring}
              />
              <InputGroup.Input
                label="Spørgsmål til en skadesag"
                value={"injury"}
              />

              <ValidationMessage
                htmlFor={"radio-subject-non-customer"}
                hidden={!(touched.subject && errors.subject)}
                invalid={!!(touched.subject && errors.subject)}
              >
                {errors.subject}
              </ValidationMessage>
            </InputGroup>
          )
        )}
        {values.subject === "injury" && (
          <InputGroup
            variant={"column"}
            id="injurySubject"
            name="subject"
            onChange={(value) => {
              if (value.at(0) !== "injury") {
                setQueueId(value.at(0) as QUEUEID);
              }
              setFieldValue("injurySubject", value.at(0));
            }}
            type="radio"
            value={queueId}
            onBlur={handleBlur}
            invalid={!!(touched.injurySubject && errors.injurySubject)}
          >
            <Legend>Hvilken forsikringen drejer henvendelsen sig om? *</Legend>
            <InputGroup.Input
              label="Bil, MC, campingvogn"
              value={QUEUEID.Bil}
            />
            <InputGroup.Input
              label="Indbo, hus, fritidshus og kæledyr"
              value={QUEUEID.IndboHusKaeledyr}
            />
            <InputGroup.Input label="Ejerskifte" value={QUEUEID.Ejerskifte} />
            <InputGroup.Input label="Rejse" value={QUEUEID.Rejse} />
            <InputGroup.Input label="Ulykke" value={QUEUEID.Ulykke} />

            <ValidationMessage
              htmlFor={"injurySubject"}
              hidden={!(touched.injurySubject && errors.injurySubject)}
              invalid={!!(touched.injurySubject && errors.injurySubject)}
            >
              {errors.injurySubject}
            </ValidationMessage>
          </InputGroup>
        )}
        <Button variant={"primary"} loading={loading} type="submit" flexible>
          Ring mig op
        </Button>
      </Flex>
    </form>
  );
};

export default CallMe;
